import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";

export default function ActualPlanInfo({
  userInfo,
  usage,
  limits,
  loading,
  purchases,
  lastPurchase,
}) {
  const [open, setOpen] = useState(false);

  const RECURRENT = "Recurrente";

  const planName = userInfo?.plan_actual || "-";
  const diasRestantes = Number(userInfo?.dias_restantes ?? -1);
  const isFree = planName === "Gratuito";

  const hasPurchases = (purchases?.length ?? 0) > 0;

  const isRecurrentActive =
    lastPurchase?.tipo_ultima_compra === RECURRENT &&
    !lastPurchase?.compra_recurrente_cancelada;

  const showDias =
    !isRecurrentActive && !isFree && hasPurchases && diasRestantes >= 0;

  const isExpired = showDias && diasRestantes <= 0;

  const diasAdeudados = Number(lastPurchase?.dias_adeudados ?? 0);

  const puntosUsed = Number(usage?.puntos_medicion ?? 0);
  const puntosLimit = Number(limits?.puntos_medicion ?? 0);
  const usersUsed = Number(usage?.usuarios_invitados ?? 0);
  const usersLimit = Number(limits?.usuarios_invitados ?? 0);

  const pct = (used, limit) => {
    if (!limit || limit <= 0) return 0;
    const v = Math.round((used / limit) * 100);
    return Math.max(0, Math.min(100, v));
  };

  const puntosPct = useMemo(() => pct(puntosUsed, puntosLimit), [puntosUsed, puntosLimit]);
  const usersPct = useMemo(() => pct(usersUsed, usersLimit), [usersUsed, usersLimit]);

  const status = useMemo(() => {
    if (loading) return { label: "Cargando…", icon: null, tone: "neutral" };

    if (diasAdeudados > 0) {
      return {
        label: `Vencido hace ${diasAdeudados} días`,
        icon: <ErrorOutlineIcon fontSize="small" />,
        tone: "danger",
      };
    }

    if (isRecurrentActive) {
      return {
        label: "Renovación automática",
        icon: <AutorenewIcon fontSize="small" />,
        tone: "info",
      };
    }

    if (showDias) {
      if (diasRestantes > 0) {
        return {
          label: `Días restantes: ${diasRestantes}`,
          icon: <EventAvailableIcon fontSize="small" />,
          tone: "success",
        };
      }
      return {
        label: "Vencido",
        icon: <ErrorOutlineIcon fontSize="small" />,
        tone: "danger",
      };
    }

    return { label: "", icon: null, tone: "neutral" };
  }, [loading, diasAdeudados, isRecurrentActive, showDias, diasRestantes]);

  const toneStyles = {
    neutral: {
      bg: "rgba(0,0,0,0.04)",
      fg: "text.secondary",
      border: "rgba(0,0,0,0.12)",
    },
    success: {
      bg: "rgba(46, 125, 50, 0.10)",
      fg: "success.main",
      border: "rgba(46, 125, 50, 0.25)",
    },
    info: {
      bg: "rgba(2, 136, 209, 0.10)",
      fg: "info.main",
      border: "rgba(2, 136, 209, 0.25)",
    },
    danger: {
      bg: "rgba(211, 47, 47, 0.10)",
      fg: "error.main",
      border: "rgba(211, 47, 47, 0.25)",
    },
  };

  const tone = toneStyles[status.tone || "neutral"];

  return (
    <Box
      sx={{
        width: { xs: "100%",sm: "46%", md: "31%", lg:'300px' },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        transition: "transform .15s ease, box-shadow .15s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 14px 40px rgba(0,0,0,0.10)",
        },
      }}
      onClick={() => setOpen((p) => !p)}
      role="button"
      aria-expanded={open}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.00) 100%)",
        }}
      >
        <Stack spacing={0.35}>
          <Typography sx={{ fontSize: 12, color: "text.secondary", marginBottom:1 }}>
            Plan actual
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <Chip
                size="small"
                label={planName}
                sx={{
                  padding: "15px 10px",
                  fontWeight: 700,
                  fontSize: 18,
                  borderRadius: 999,
                  bgcolor: "rgba(0,0,0,0.06)",
                }}
              />
            )
            }

          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">         

          <ArrowDropDownIcon
            sx={{
              transition: "transform .2s ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: "text.secondary",
            }}
          />
        </Stack>
      </Box>

      {/* Status pill */}
      {status.label ? (
        <Box sx={{ px: 2, pb: 1.25 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.2,
              py: 0.6,
              borderRadius: 999,
              border: "1px solid",
              borderColor: tone.border,
              bgcolor: tone.bg,
              color: tone.fg,
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            {status.icon}
            {status.label}
          </Box>
        </Box>
      ) : (
        <Box sx={{ px: 2, pb: 1.25 }} />
      )}

      <Divider />

      {/* More info */}
      <Collapse in={open} timeout={220} unmountOnExit>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack spacing={1.3}>
            {/* Puntos medición */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <SpeedOutlinedIcon fontSize="small" />
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    Puntos medición
                  </Typography>
                </Stack>

                <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 700 }}>
                  {puntosUsed}/{puntosLimit || "-"}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={puntosPct}
                sx={{
                  mt: 0.8,
                  height: 8,
                  borderRadius: 999,
                  bgcolor: "rgba(0,0,0,0.06)",
                  "& .MuiLinearProgress-bar": { borderRadius: 999 },
                }}
              />
              <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
                {puntosLimit ? `${puntosPct}% usado` : "Sin límite definido"}
              </Typography>
            </Box>

            {/* Usuarios invitados */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <GroupsOutlinedIcon fontSize="small" />
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    Usuarios invitados
                  </Typography>
                </Stack>

                <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 700 }}>
                  {usersUsed}/{usersLimit || "-"}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={usersPct}
                sx={{
                  mt: 0.8,
                  height: 8,
                  borderRadius: 999,
                  bgcolor: "rgba(0,0,0,0.06)",
                  "& .MuiLinearProgress-bar": { borderRadius: 999 },
                }}
              />
              <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
                {usersLimit ? `${usersPct}% usado` : "Sin límite definido"}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}